import type { DecisionEngineJourneyDto } from '../api/admin';
import type { TabNarrative } from './applicationTabNarratives';
import { isDecisionEngineHardDecline } from './decisionEngineDeclineCopy';
import { buildPdlDeclineExplanation } from './pdlDeclineExplainer';
import { buildPdlEligibilityExplanation } from './pdlEligibilityExplainer';

/** Plain-language "What's happening" for the PDL Decision Engine tab. */
export function buildPdlDecisionTabNarrative(input: {
  decisionEngine?: DecisionEngineJourneyDto | null;
  declineReason?: string | null;
  applicationStatus?: string | null;
}): TabNarrative {
  const explanation = buildPdlEligibilityExplanation(
    input.decisionEngine,
    input.declineReason ?? null,
  );
  const decision = String(
    input.decisionEngine?.decision ?? explanation?.decision ?? '',
  ).toUpperCase();

  const declined = isDecisionEngineHardDecline({
    decision: input.decisionEngine?.decision ?? explanation?.decision,
    applicationDeclineReason: input.declineReason,
    applicationStatus: input.applicationStatus,
  });

  if (declined) {
    const explained = buildPdlDeclineExplanation({
      decisionEngine: input.decisionEngine,
      applicationDeclineReason: input.declineReason,
    });
    return {
      headline: explained.headline,
      tone: 'error',
      detail: explained.why,
      bullets: explained.notes,
      calculation: explained.calculation,
      checkLink: explained.check,
      technicalDetails: explained.technical,
    };
  }

  if (/REFER|REVIEW|CHALLENGE/.test(decision)) {
    return {
      headline: 'Payday decision needs manual review.',
      tone: 'warning',
      detail: 'Decision Engine returned REFER — check Manual review before contacting the customer.',
      bullets: [],
    };
  }

  if (/APPROV/.test(decision) && explanation) {
    const approved = explanation.amountComparison.approved;
    const requested = explanation.amountComparison.requested;
    let detail: string | null = null;
    if (approved && requested && approved !== requested) {
      detail = `Approved ${approved} (customer requested ${requested}).`;
    } else if (approved) {
      detail = `Approved offer: ${approved}.`;
    } else if (explanation.amountComparison.why) {
      detail = explanation.amountComparison.why;
    }
    return {
      headline: 'Payday offer approved.',
      tone: 'success',
      detail,
      bullets: [],
    };
  }

  return {
    headline: 'Payday capacity evaluation is on file.',
    tone: 'info',
    detail: null,
    bullets: [],
  };
}
